import { useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { ComposeModal } from "../../../../../components/dashboard/compose";
import { parseInboxDirection } from "@/features/inbox/filters";
import { sendComposeEmail } from "@/features/inbox/server-fns";
import {
  composeOptionsQueryOptions,
  inboxThreadQueryKey,
  inboxThreadQueryOptions,
} from "@/features/inbox/queries";
import { capturePostHogEvent } from "@/lib/posthog-client";
import { settingsUsageOverviewQueryOptions } from "@/features/settings/queries";

export const Route = createFileRoute("/dashboard/inbox/$id/reply/")({
  validateSearch: (search: Record<string, unknown>) => ({
    domainId: (search.domainId as string) || undefined,
    aliasId: (search.aliasId as string) || undefined,
    direction: parseInboxDirection(search.direction),
    q: (search.q as string) || undefined,
  }),
  head: () => ({
    meta: [{ title: "Reply - Bavimail" }],
  }),
  staleTime: 15_000,
  preloadStaleTime: 15_000,
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(composeOptionsQueryOptions());
  },
  component: DashboardInboxReplyRoute,
});

function DashboardInboxReplyRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = Route.useParams();
  const { domainId, aliasId, direction, q } = Route.useSearch();
  const { data: aliases } = useSuspenseQuery(composeOptionsQueryOptions());
  const usageOverviewQuery = useQuery({
    ...settingsUsageOverviewQueryOptions(),
    retry: false,
    throwOnError: false,
  });
  const { data: thread } = useSuspenseQuery(inboxThreadQueryOptions(id));

  return (
    <ComposeModal
      mode="reply"
      aliases={aliases}
      showBavimailFooter={!usageOverviewQuery.data?.subscription.isPaidPlan}
      thread={thread}
      onClose={() =>
        navigate({
          to: "/dashboard/inbox/$id",
          params: { id },
          search: { domainId, aliasId, direction, q },
        })
      }
      onSend={async (input) => {
        const result = await sendComposeEmail({ data: input });
        capturePostHogEvent("email_sent", { mode: "reply" });
        const targetConversationId = result.conversationId || id;
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: inboxThreadQueryKey(targetConversationId) }),
          queryClient.invalidateQueries({ queryKey: ["inboxThreads"] }),
        ]);
        await navigate({
          to: "/dashboard/inbox/$id",
          params: { id: targetConversationId },
          search: { domainId, aliasId, direction, q },
        });
      }}
    />
  );
}
